import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { COLOR_BANTAMWEIGHT } from '@utilities/helpers/colors';

import {
  buildConnectorPaths,
  DECOMPOSITION_LAYOUT,
  ROW_COUNT,
  ROW_GAP,
} from '../../../../utilities/decomposition_tree_helpers';

import Control from '../Control';
import Node from '../Node';

import styles from './_index.module.scss';

const DecompositionTree = ({
  answers,
  askWhyInsightsDocument,
  askWhyInstantSurveys,
  askWhySurveyType,
  data,
  documentId,
  exportData,
  hasPlaceholders = false,
  isReadOnly,
  nodeClassName,
  onAskWhySubmit,
  prescriptText,
  reportId,
  showExportMenu = true,
}) => {
  const nodes = data?.cardBlocks?.treeNodes || [];
  const treeRef = useRef(null);
  const containerRef = useRef(null);
  const cardRefs = useRef({});
  const [geometry, setGeometry] = useState({
    height: 0,
    heights: {},
    paths: [],
    signature: '',
    tops: {},
  });

  const nodesById = nodes.reduce((acc, node) => {
    if (node?.nodeId) acc[node.nodeId] = node;
    return acc;
  }, {});
  const nodeIds = Object.keys(nodesById).sort().join('|');

  const measure = useCallback(() => {
    if (!containerRef.current) return;

    const cardHeight = {};
    const rowMaxHeight = Array(ROW_COUNT).fill(0);
    Object.entries(DECOMPOSITION_LAYOUT).forEach(([id, { row }]) => {
      const el = cardRefs.current[id];
      if (!el) return;
      // Read the intrinsic height with the imposed min-height cleared, then restore it,
      // so a row can shrink back when its text unwraps (offsetHeight would otherwise
      // report the previously applied min-height).
      const applied = el.style.minHeight;
      el.style.minHeight = '0px';
      const intrinsic = el.offsetHeight;
      el.style.minHeight = applied;
      cardHeight[id] = intrinsic;
      rowMaxHeight[row] = Math.max(rowMaxHeight[row], intrinsic);
    });

    const rowTop = [];
    let offset = 0;
    for (let row = 0; row < ROW_COUNT; row += 1) {
      rowTop[row] = offset;
      offset += rowMaxHeight[row] + ROW_GAP;
    }
    const height = Math.max(0, offset - ROW_GAP);

    const tops = {};
    const heights = {};
    Object.entries(DECOMPOSITION_LAYOUT).forEach(([id, { row }]) => {
      tops[id] = rowTop[row];
      heights[id] = rowMaxHeight[row];
    });
    const paths = buildConnectorPaths(rowTop, rowMaxHeight, new Set(Object.keys(cardHeight)));

    // Bail out when nothing changed: applying height/min-height re-triggers the observer,
    // so a stable signature prevents an update loop.
    const signature = `${height}|${paths.join('|')}|${rowMaxHeight.join(',')}`;
    setGeometry((prev) =>
      prev.signature === signature ? prev : { height, heights, paths, signature, tops }
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      let frame = 0;
      const observer = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(measure);
      });
      Object.values(cardRefs.current).forEach((card) => {
        if (card) observer.observe(card);
      });
      return () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
      };
    }
    // Fallback for environments without ResizeObserver (jsdom tests, older browsers).
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, nodeIds]);

  return (
    <>
      {showExportMenu && (
        <Control exportData={exportData} prescriptText={prescriptText} treeRef={treeRef} />
      )}
      <div className="tree-wrapper" ref={treeRef}>
        <div
          className={styles['decomposition-tree']}
          ref={containerRef}
          style={geometry.height ? { height: geometry.height } : undefined}
        >
          <svg
            aria-hidden="true"
            className={styles['connectors']}
            preserveAspectRatio="none"
            viewBox={`0 0 100 ${geometry.height || 0}`}
          >
            {geometry.paths.map((d, index) => (
              <path
                d={d}
                fill="none"
                key={`connector-${index}`}
                stroke={COLOR_BANTAMWEIGHT}
                strokeDasharray="2 4"
                strokeLinecap="round"
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
          {Object.entries(DECOMPOSITION_LAYOUT).map(([id, { cx }]) => {
            const node = nodesById[id];
            if (!node) return null;
            return (
              <div
                className={styles['decomposition-node']}
                key={id}
                ref={(element) => {
                  cardRefs.current[id] = element;
                }}
                style={{
                  left: `${cx}%`,
                  minHeight: geometry.heights[id] || undefined,
                  top: geometry.tops[id] || 0,
                }}
              >
                <Node
                  answers={answers}
                  askWhyInsightsDocument={askWhyInsightsDocument}
                  askWhyInstantSurveys={askWhyInstantSurveys}
                  askWhySurveyType={askWhySurveyType}
                  className={nodeClassName}
                  data={node}
                  documentId={documentId}
                  hasPlaceholders={hasPlaceholders}
                  isReadOnly={isReadOnly}
                  onAskWhySubmit={onAskWhySubmit}
                  reportId={reportId}
                  reportName={exportData?.reportName}
                  type="tree"
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

DecompositionTree.propTypes = {
  answers: PropTypes.object,
  askWhyInsightsDocument: PropTypes.object,
  askWhyInstantSurveys: PropTypes.array,
  askWhySurveyType: PropTypes.object,
  data: PropTypes.object,
  documentId: PropTypes.string,
  exportData: PropTypes.object,
  hasPlaceholders: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  nodeClassName: PropTypes.string,
  onAskWhySubmit: PropTypes.func,
  prescriptText: PropTypes.node,
  reportId: PropTypes.number,
  showExportMenu: PropTypes.bool,
};

export default DecompositionTree;
